// Lote E: ficha de un accidente o de una presunción de enfermedad profesional con el aviso al IESS (PO 9 y PO 12) y
// sus medidas correctivas (PO 10 y PO 13). StorSafe no envía el aviso: registra el número, la fecha de envío y la
// constancia, y controla el término de 10 días hábiles. Cada cambio vuelve a calcular el Checklist (inc-datos.ts).
// Pensada para 360 px: todo en una columna que se acomoda (flex-wrap), sin tablas.

import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { abrirArchivo } from '../../lib/archivos';
import {
  ACEPTA_ARCHIVO, BUCKET_INCIDENTES, NIVELES_CONTROL, ORIGENES_PRESUNCION, borrarAviso, borrarMedida, cerrarMedida, crearMedida,
  actualizarMedida, editableDesdeFila, etiquetaNivel, imagenParaPdf, listarMedidas, medidaVacia, reabrirMedida, registrarAviso,
  validarArchivo, validarAviso, validarCierre, validarMedida, validarReapertura, type FilaMedida, type MedidaEditable,
} from '../../lib/incidentes/inc-datos';
import {
  ESTADO_CERRADA, ETIQUETA_AVISO, ETIQUETA_MEDIDA, NOTA_PLAZO_MEDIDAS, estadoAvisoCaso, estadoMedida, estadoMedidasCaso, tipoCaso,
  type EstadoAviso, type EstadoMedida, type EstadoMedidasCaso,
} from '../../lib/incidentes/inc-estado';
import { generarCasoPDF } from '../../lib/incidentes/inc-pdf';
import { NOTA_TERMINO, NOTA_TERMINO_ENFERMEDAD, fechaCorta, hoyIso, textoVence } from '../../lib/incidentes/inc-plazos';
import { INCIDENT_TYPE_LABELS, SEVERITY_LABELS, type Incident } from './IncidentModal';

interface ProfileOption { id: string; full_name: string | null; email: string | null }

interface Props {
  companyId: string;
  companyName: string;
  incident: Incident;
  workerName: string | null;
  warehouseName: string | null;
  canEdit: boolean;
  onChanged: () => void;
  onClose: () => void;
}

const COLOR_AVISO: Record<EstadoAviso, string> = { con_constancia: '#22c55e', fuera_de_termino: '#f59e0b', en_termino: '#38bdf8', vencido: '#ef4444' };
const COLOR_MEDIDAS_CASO: Record<EstadoMedidasCaso, string> = { cumple: '#22c55e', en_proceso: '#38bdf8', no_cumple: '#ef4444' };
const ETIQUETA_MEDIDAS_CASO: Record<EstadoMedidasCaso, string> = { cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple' };
const COLOR_MEDIDA: Record<EstadoMedida, string> = { cerrada: '#22c55e', abierta: '#38bdf8', vencida: '#ef4444', sin_fecha: '#f59e0b' };

export default function IncidentAvisoMedidasModal({ companyId, companyName, incident, workerName, warehouseName, canEdit, onChanged, onClose }: Props) {
  const hoy = hoyIso();
  const enfermedad = tipoCaso(incident.incident_type) === 'enfermedad';
  const [caso, setCaso] = useState<Incident>(incident);
  const [medidas, setMedidas] = useState<FilaMedida[]>([]);
  const [perfiles, setPerfiles] = useState<ProfileOption[]>([]);
  const [conInvestigacion, setConInvestigacion] = useState(false);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  // Aviso
  const [numero, setNumero] = useState('');
  const [fechaEnvio, setFechaEnvio] = useState('');
  const [archivoAviso, setArchivoAviso] = useState<File | null>(null);

  // Medidas
  const [nueva, setNueva] = useState<MedidaEditable | null>(null);
  const [editando, setEditando] = useState<{ id: string; m: MedidaEditable; seguimiento: boolean } | null>(null);
  const [cerrando, setCerrando] = useState<{ id: string; archivo: File | null } | null>(null);
  const [reabriendo, setReabriendo] = useState<{ id: string; motivo: string } | null>(null);

  const cargar = useCallback(async () => {
    const [{ data: inc }, lista, { data: profs }, { count }] = await Promise.all([
      supabase.from('incidents').select('*').eq('id', incident.id).maybeSingle(),
      listarMedidas(incident.id).catch(e => { setError(e instanceof Error ? e.message : String(e)); return [] as FilaMedida[]; }),
      supabase.from('profiles').select('id, full_name, email').eq('company_id', companyId).eq('active', true).order('full_name'),
      supabase.from('incident_investigations').select('id', { count: 'exact', head: true }).eq('incident_id', incident.id),
    ]);
    if (inc) setCaso(inc as Incident);
    setMedidas(lista);
    setPerfiles((profs ?? []) as ProfileOption[]);
    setConInvestigacion((count ?? 0) > 0);
  }, [incident.id, companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const nombrePerfil = (id: string | null) => {
    const p = perfiles.find(x => x.id === id);
    return p ? (p.full_name || p.email || '') : '';
  };
  const responsable = (m: FilaMedida) => [nombrePerfil(m.responsible_id), m.responsible_name ?? ''].filter(Boolean).join(' · ') || '—';

  const correr = async (fn: () => Promise<void>) => {
    setOcupado(true); setError('');
    try { await fn(); await cargar(); onChanged(); return true; }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); return false; }
    finally { setOcupado(false); }
  };

  const aviso = estadoAvisoCaso(caso, hoy);
  const medidasCaso = estadoMedidasCaso(caso, medidas, hoy);
  const tieneAviso = !!caso.iess_constancia_path;

  async function guardarAviso() {
    const errores = validarAviso({ numero, fechaEnvio, archivo: archivoAviso }, caso.incident_date, hoy);
    if (errores.length) { setError(errores.join(' ')); return; }
    const ok = await correr(() => registrarAviso(companyId, caso.id, { numero, fechaEnvio, archivo: archivoAviso }, caso.iess_constancia_path ?? null));
    if (ok) { setNumero(''); setFechaEnvio(''); setArchivoAviso(null); }
  }

  async function quitarAviso() {
    if (!window.confirm('¿Borrar el aviso registrado (número, fecha y constancia)?')) return;
    await correr(() => borrarAviso(companyId, caso.id, caso.iess_constancia_path ?? null));
  }

  async function guardarNueva() {
    if (!nueva) return;
    const errores = validarMedida(nueva);
    if (errores.length) { setError(errores.join(' ')); return; }
    const ok = await correr(async () => { await crearMedida(companyId, caso.id, nueva); });
    if (ok) setNueva(null);
  }

  async function guardarEdicion(f: FilaMedida) {
    if (!editando) return;
    const errores = validarMedida(editando.m);
    if (errores.length) { setError(errores.join(' ')); return; }
    const ok = await correr(() => actualizarMedida(companyId, f, editando.m, editando.seguimiento));
    if (ok) setEditando(null);
  }

  async function confirmarCierre(f: FilaMedida) {
    const archivo = cerrando?.id === f.id ? cerrando.archivo : null;
    const motivo = validarCierre(f, archivo);
    if (motivo) { setError(motivo); return; }
    const ok = await correr(() => cerrarMedida(companyId, f, archivo));
    if (ok) setCerrando(null);
  }

  async function confirmarReapertura(f: FilaMedida) {
    if (!reabriendo) return;
    const e = validarReapertura(reabriendo.motivo);
    if (e) { setError(e); return; }
    const ok = await correr(() => reabrirMedida(companyId, f, reabriendo.motivo));
    if (ok) setReabriendo(null);
  }

  async function quitarMedida(f: FilaMedida) {
    if (!window.confirm('¿Borrar esta medida correctiva y su evidencia?')) return;
    await correr(() => borrarMedida(companyId, f));
  }

  async function descargarPDF() {
    setOcupado(true); setError('');
    try {
      const imagenes: Record<string, string | null> = { aviso: await imagenParaPdf(caso.iess_constancia_path, caso.iess_constancia_mime) };
      for (const m of medidas) imagenes[m.id] = await imagenParaPdf(m.evidence_path, m.evidence_mime);
      const origen = ORIGENES_PRESUNCION.find(o => o.valor === caso.disease_origin)?.etiqueta;
      const doc = generarCasoPDF({
        empresa: companyName, hoy, fecha: new Date(), imagenes,
        caso: {
          ...caso, tipoEtiqueta: enfermedad ? 'Presunción de enfermedad profesional' : INCIDENT_TYPE_LABELS[caso.incident_type],
          severity: caso.severity ? SEVERITY_LABELS[caso.severity] : null, trabajador: workerName, lugar: warehouseName,
          investigacion: conInvestigacion, origen: [origen, caso.disease_origin_detail].filter(Boolean).join(': ') || null,
        },
        medidas: medidas.map(m => ({ ...m, responsable: responsable(m) })),
      });
      doc.save(`caso-${caso.incident_date}.pdf`);
    } catch (e) {
      setError('No se pudo generar el PDF: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setOcupado(false);
    }
  }

  const formMedida = (m: MedidaEditable, cambiar: (m: MedidaEditable) => void) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Campo etiqueta="Medida *">
        <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={m.descripcion} onChange={e => cambiar({ ...m, descripcion: e.target.value })}
          placeholder="Qué se va a hacer para evitar que se repita" />
      </Campo>
      <Campo etiqueta="Nivel en la jerarquía de controles *">
        <select style={inp} value={m.nivel} onChange={e => cambiar({ ...m, nivel: e.target.value as MedidaEditable['nivel'] })}>
          <option value="">Elige el nivel…</option>
          {NIVELES_CONTROL.map(n => <option key={n.valor} value={n.valor}>{n.etiqueta}</option>)}
        </select>
      </Campo>
      <div style={fila}>
        <Campo etiqueta="Responsable (usuario)">
          <select style={inp} value={m.responsableId} onChange={e => cambiar({ ...m, responsableId: e.target.value })}>
            <option value="">Sin usuario</option>
            {perfiles.map(p => <option key={p.id} value={p.id}>{p.full_name || p.email}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="o nombre y cargo">
          <input style={inp} value={m.responsableNombre} onChange={e => cambiar({ ...m, responsableNombre: e.target.value })} placeholder="p. ej. Jefe de bodega" />
        </Campo>
      </div>
      <div style={fila}>
        <Campo etiqueta="Fecha compromiso *">
          <input style={inp} type="date" value={m.fecha} onChange={e => cambiar({ ...m, fecha: e.target.value })} />
        </Campo>
      </div>
      <Campo etiqueta="Seguimiento">
        <textarea style={{ ...inp, minHeight: 44, resize: 'vertical' }} value={m.seguimiento} onChange={e => cambiar({ ...m, seguimiento: e.target.value })}
          placeholder="Avance, revisiones y observaciones" />
      </Campo>
    </div>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label="Aviso al IESS y medidas correctivas"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '12px 8px', overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 640, margin: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, padding: '16px', borderBottom: '1px solid #334155' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Aviso al IESS y medidas correctivas</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '4px 0 0', overflowWrap: 'anywhere' }}>
              {enfermedad ? 'Presunción de enfermedad profesional' : INCIDENT_TYPE_LABELS[caso.incident_type]} · {fechaCorta(caso.incident_date)}
              {workerName ? ` · ${workerName}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} style={btnCerrar} aria-label="Cerrar">✕</button>
        </div>

        <div style={{ padding: '0 16px 16px' }}>
          {error && <p role="alert" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5', borderRadius: 8, padding: '8px 10px', fontSize: '0.82rem', margin: '12px 0 0' }}>{error}</p>}

          {/* ── Aviso al IESS ── */}
          <Seccion titulo="Aviso al IESS">
            <div style={fila}>
              <Dato etiqueta={enfermedad ? 'Indicios advertidos el' : 'Fecha del siniestro'} valor={fechaCorta(caso.incident_date)} />
              <Dato etiqueta="Fecha límite del aviso" valor={fechaCorta(aviso.fechaLimite)} />
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '0.84rem', fontWeight: 700, color: COLOR_AVISO[aviso.estado] }}>
              {ETIQUETA_AVISO[aviso.estado]}
              {(aviso.estado === 'en_termino' || aviso.estado === 'vencido') && ` · ${textoVence(aviso.fechaLimite, hoy)}`}
            </p>
            <p style={nota}>{enfermedad ? NOTA_TERMINO_ENFERMEDAD : NOTA_TERMINO} StorSafe no envía el aviso: regístralo aquí después de presentarlo en línea.</p>
            {tieneAviso && (
              <div style={{ ...tarjeta, marginTop: 10 }}>
                <div style={fila}>
                  <Dato etiqueta="Número de aviso" valor={caso.iess_report_number || '—'} />
                  <Dato etiqueta="Fecha de envío" valor={fechaCorta(caso.iess_report_date)} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <button type="button" style={btnSec} onClick={() => abrirArchivo(BUCKET_INCIDENTES, caso.iess_constancia_path)}>Ver constancia</button>
                  {canEdit && <button type="button" style={btnPeligro} disabled={ocupado} onClick={quitarAviso}>Borrar aviso</button>}
                </div>
                <p style={nota}>La constancia de un aviso registrado está protegida. Para cambiarla, borra el aviso y regístralo de nuevo.</p>
              </div>
            )}
            {canEdit && !tieneAviso && (
              <div style={{ ...tarjeta, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={fila}>
                  <Campo etiqueta="Número de aviso *"><input style={inp} value={numero} onChange={e => setNumero(e.target.value)} placeholder="Número que da el sistema del IESS" /></Campo>
                  <Campo etiqueta="Fecha de envío *"><input style={inp} type="date" value={fechaEnvio} max={hoy} onChange={e => setFechaEnvio(e.target.value)} /></Campo>
                </div>
                <Campo etiqueta="Constancia (PDF, PNG o JPEG, hasta 10 MB) *">
                  <input type="file" accept={ACEPTA_ARCHIVO} style={{ ...inp, padding: 6 }}
                    onChange={e => { const f = e.target.files?.[0] ?? null; setArchivoAviso(f); setError(f ? validarArchivo(f) ?? '' : ''); }} />
                </Campo>
                <div><button type="button" style={btnPrim} disabled={ocupado} onClick={guardarAviso}>{ocupado ? 'Guardando…' : 'Registrar aviso'}</button></div>
              </div>
            )}
          </Seccion>

          {/* ── Medidas correctivas ── */}
          <Seccion titulo="Medidas correctivas">
            <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 700, color: COLOR_MEDIDAS_CASO[medidasCaso.estado] }}>
              {ETIQUETA_MEDIDAS_CASO[medidasCaso.estado]}
              {medidasCaso.estado === 'en_proceso' && ` · plazo para definir medidas hasta el ${fechaCorta(medidasCaso.finPlazo)}`}
            </p>
            <p style={nota}>{NOTA_PLAZO_MEDIDAS}</p>
            <p style={nota}>Cada medida necesita nivel de control, responsable y fecha compromiso. Se cierra solo con evidencia (foto o archivo).</p>
            {medidas.length === 0 && <p style={{ color: '#fca5a5', fontSize: '0.84rem', margin: '8px 0 0' }}>Este caso todavía no tiene medidas correctivas.</p>}
            {medidas.map(f => {
              const e = estadoMedida(f, hoy);
              const cerrada = f.status === ESTADO_CERRADA;
              return (
                <div key={f.id} style={{ ...tarjeta, marginTop: 10 }}>
                  {editando?.id === f.id ? (
                    <>
                      {formMedida(editando.m, m => setEditando({ ...editando, m }))}
                      {!cerrada && (
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#cbd5e1', fontSize: '0.82rem', marginTop: 8 }}>
                          <input type="checkbox" checked={editando.seguimiento} onChange={ev => setEditando({ ...editando, seguimiento: ev.target.checked })} /> En seguimiento
                        </label>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                        <button type="button" style={btnPrim} disabled={ocupado} onClick={() => guardarEdicion(f)}>Guardar</button>
                        <button type="button" style={btnSec} onClick={() => setEditando(null)}>Cancelar</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p style={{ color: '#f1f5f9', fontSize: '0.88rem', fontWeight: 600, margin: 0, overflowWrap: 'anywhere' }}>{f.description}</p>
                      <p style={{ color: COLOR_MEDIDA[e], fontSize: '0.78rem', fontWeight: 700, margin: '4px 0 0' }}>
                        {ETIQUETA_MEDIDA[e]}{f.status === 'en_proceso' && e !== 'cerrada' ? ' · en seguimiento' : ''}
                        {e === 'abierta' || e === 'vencida' ? ` · ${textoVence(f.due_date as string, hoy)}` : ''}
                      </p>
                      <div style={{ ...fila, marginTop: 6 }}>
                        <Dato etiqueta="Jerarquía de controles" valor={etiquetaNivel(f.control_level)} />
                        <Dato etiqueta="Responsable" valor={responsable(f)} />
                        <Dato etiqueta="Fecha compromiso" valor={fechaCorta(f.due_date)} />
                        {cerrada && <Dato etiqueta="Cerrada el" valor={fechaCorta(f.completed_at)} />}
                      </div>
                      {f.follow_up && <p style={{ color: '#cbd5e1', fontSize: '0.8rem', margin: '6px 0 0', overflowWrap: 'anywhere' }}>Seguimiento: {f.follow_up}</p>}
                      {f.reopen_reason && <p style={nota}>Última reapertura: {f.reopen_reason}</p>}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {f.evidence_path && <button type="button" style={btnSec} onClick={() => abrirArchivo(BUCKET_INCIDENTES, f.evidence_path)}>Ver evidencia</button>}
                        {canEdit && <button type="button" style={btnSec} disabled={ocupado} onClick={() => { setEditando({ id: f.id, m: editableDesdeFila(f), seguimiento: f.status === 'en_proceso' }); setError(''); }}>Editar</button>}
                        {canEdit && !cerrada && <button type="button" style={btnPrim} disabled={ocupado} onClick={() => { setCerrando({ id: f.id, archivo: null }); setError(''); }}>Cerrar con evidencia</button>}
                        {canEdit && cerrada && <button type="button" style={btnSec} disabled={ocupado} onClick={() => { setReabriendo({ id: f.id, motivo: '' }); setError(''); }}>Reabrir</button>}
                        {canEdit && !cerrada && <button type="button" style={btnPeligro} disabled={ocupado} onClick={() => quitarMedida(f)}>Borrar</button>}
                      </div>
                      {cerrada && <p style={nota}>La evidencia de una medida cerrada está protegida. Para cambiarla o borrar la medida, primero reábrela con un motivo.</p>}
                      {cerrando?.id === f.id && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Campo etiqueta={f.evidence_path ? 'Evidencia nueva (opcional; ya tiene una)' : 'Evidencia del cierre (foto o archivo, hasta 10 MB) *'}>
                            <input type="file" accept={ACEPTA_ARCHIVO} style={{ ...inp, padding: 6 }}
                              onChange={ev => { const a = ev.target.files?.[0] ?? null; setCerrando({ id: f.id, archivo: a }); setError(a ? validarArchivo(a) ?? '' : ''); }} />
                          </Campo>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <button type="button" style={btnPrim} disabled={ocupado} onClick={() => confirmarCierre(f)}>Confirmar cierre</button>
                            <button type="button" style={btnSec} onClick={() => setCerrando(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}
                      {reabriendo?.id === f.id && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Campo etiqueta="Motivo para reabrir *">
                            <input style={inp} value={reabriendo.motivo} onChange={ev => setReabriendo({ id: f.id, motivo: ev.target.value })} />
                          </Campo>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <button type="button" style={btnPrim} disabled={ocupado} onClick={() => confirmarReapertura(f)}>Reabrir</button>
                            <button type="button" style={btnSec} onClick={() => setReabriendo(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {canEdit && (nueva ? (
              <div style={{ ...tarjeta, marginTop: 10 }}>
                {formMedida(nueva, setNueva)}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  <button type="button" style={btnPrim} disabled={ocupado} onClick={guardarNueva}>Guardar medida</button>
                  <button type="button" style={btnSec} onClick={() => setNueva(null)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button type="button" style={{ ...btnSec, marginTop: 10 }} onClick={() => { setNueva(medidaVacia()); setError(''); }}>+ Agregar medida</button>
            ))}
          </Seccion>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" style={btnSec} disabled={ocupado} onClick={descargarPDF}>Descargar PDF del caso</button>
            <button type="button" style={btnSec} onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 16 }}>
      <h3 style={{ color: '#38bdf8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>{titulo}</h3>
      {children}
    </section>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px', minWidth: 0 }}>
      <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600 }}>{etiqueta}</span>
      {children}
    </label>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
      <span style={{ display: 'block', color: '#64748b', fontSize: '0.72rem' }}>{etiqueta}</span>
      <span style={{ color: '#e2e8f0', fontSize: '0.84rem', overflowWrap: 'anywhere' }}>{valor}</span>
    </div>
  );
}

const fila: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 10 };
const nota: React.CSSProperties = { color: '#64748b', fontSize: '0.74rem', margin: '6px 0 0', lineHeight: 1.4 };
const tarjeta: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: 10, padding: 12 };
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', background: 'rgba(255,255,255,0.05)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', minWidth: 0 };
const btnPrim: React.CSSProperties = { padding: '8px 14px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.82rem', borderRadius: 8, cursor: 'pointer', minHeight: 36 };
const btnSec: React.CSSProperties = { padding: '8px 12px', background: 'transparent', border: '1px solid #475569', color: '#cbd5e1', fontSize: '0.82rem', borderRadius: 8, cursor: 'pointer', minHeight: 36 };
const btnPeligro: React.CSSProperties = { ...btnSec, borderColor: 'rgba(239,68,68,0.5)', color: '#fca5a5' };
const btnCerrar: React.CSSProperties = { background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.1rem', cursor: 'pointer', minWidth: 36, minHeight: 36 };
